import * as React from "react"
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles"

import { CssBaseline, GlobalStyles } from "@mui/material"
import zIndexMap from "util/z-index-map"

// -------------------------------
// TypeScript Module Augmentation
// -------------------------------
declare module "@mui/material/styles" {
  // eslint-disable-next-line no-unused-vars
  interface TypographyVariants {
    xs: number | string
    sm: number | string
    md: number | string
    lg: number | string
    xl: number | string
    "2xl": number | string
    "3xl": number | string
    "4xl": number | string
    "5xl": number | string
    "6xl": number | string
    "7xl": number | string
  }
  // eslint-disable-next-line no-unused-vars
  interface TypographyVariantsOptions {
    xs?: number | string
    sm?: number | string
    md?: number | string
    lg?: number | string
    xl?: number | string
    "2xl"?: number | string
    "3xl"?: number | string
    "4xl"?: number | string
    "5xl"?: number | string
    "6xl"?: number | string
    "7xl"?: number | string
  }
}
declare module "@mui/material/Typography" {
  // eslint-disable-next-line no-unused-vars
  interface TypographyPropsVariantOverrides {
    // add simple numeric font sizes similar to how Styled System used them
    xs: true
    sm: true
    md: true
    lg: true
    xl: true
    "2xl": true
    "3xl": true
    "4xl": true
    "5xl": true
    "6xl": true
    "7xl": true
    // disable some unused variant types
    body1: false
    body2: false
    caption: false
    overline: false
    // reduce total definitions for headers so they aren't overused
    h5: false
    h6: false
  }
}

declare module "@mui/material/styles/createPalette" {
  interface ColorRange {
    50: string
    100: string
    200: string
    300: string
    400: string
    500: string
    600: string
    700: string
    800: string
    900: string
  }
  // eslint-disable-next-line no-unused-vars
  interface PaletteOptions {
    dataViz: ColorRange
    yellow: ColorRange
    green: ColorRange
    red: ColorRange
    fuchsia: ColorRange
    violet: ColorRange
    blue: ColorRange
    gray: ColorRange
  }
  // eslint-disable-next-line no-unused-vars
  interface PaletteColor extends ColorRange {
    subtle?: string
  }
  // eslint-disable-next-line no-unused-vars
  interface Palette {
    dataViz: ColorRange
    yellow: ColorRange
    green: ColorRange
    red: ColorRange
    fuchsia: ColorRange
    violet: ColorRange
    blue: ColorRange
    gray: ColorRange
  }
}

declare module "@mui/material/Button" {
  // eslint-disable-next-line no-unused-vars
  interface ButtonPropsColorOverrides {
    gray: true // allow us to use gray or secondary on button color props
  }
  // eslint-disable-next-line no-unused-vars
  interface ButtonPropsVariantOverrides {
    soft: true
  }
}

// -------------------------------
// Define some custom JSX elements
// -------------------------------
const radioCheckedIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM13.7071 8.70711C14.0976 8.31658 14.0976 7.68342 13.7071 7.29289C13.3166 6.90237 12.6834 6.90237 12.2929 7.29289L9 10.5858L7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L8.29289 12.7071C8.68342 13.0976 9.31658 13.0976 9.70711 12.7071L13.7071 8.70711Z"
      fill="currentColor"
    />
  </svg>
)
const radioUncheckedIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="2.5"
      y="2.5"
      width="15"
      height="15"
      rx="7.5"
      stroke="currentColor"
    />
  </svg>
)

const checkboxIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="2.5"
      y="2.5"
      width="15"
      height="15"
      rx="2.5"
      stroke="currentColor"
      strokeWidth={1.25}
    />
  </svg>
)
const checkboxIndeterminateIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5 2C3.34315 2 2 3.34315 2 5V15C2 16.6569 3.34315 18 5 18H15C16.6569 18 18 16.6569 18 15V5C18 3.34315 16.6569 2 15 2H5ZM7 9C6.44772 9 6 9.44772 6 10C6 10.5523 6.44772 11 7 11H13C13.5523 11 14 10.5523 14 10C14 9.44772 13.5523 9 13 9H7Z"
      fill="currentColor"
    />
  </svg>
)
const checkboxCheckedIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5 2C3.34315 2 2 3.34315 2 5V15C2 16.6569 3.34315 18 5 18H15C16.6569 18 18 16.6569 18 15V5C18 3.34315 16.6569 2 15 2H5ZM13.7071 8.70711C14.0976 8.31658 14.0976 7.68342 13.7071 7.29289C13.3166 6.90237 12.6834 6.90237 12.2929 7.29289L9 10.5858L7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L8.29289 12.7071C8.68342 13.0976 9.31658 13.0976 9.70711 12.7071L13.7071 8.70711Z"
      fill="currentColor"
    />
  </svg>
)

// -------------------------------
// Define shared tokens/constants
// -------------------------------
const primaryColorScale = {
  50: "#eff6ff",
  100: "#dbeafe",
  200: "#bfdbfe",
  300: "#93c5fd",
  400: "#60a5fa",
  500: "#3b82f6",
  600: "#2563eb",
  main: "#2563eb",
  700: "#1d4ed8",
  800: "#1e40af",
  900: "#1e3a8a",
  contrastText: "#f9fafb",
  subtle: "hsla(217, 83%, 50%, 0.1)",
}
const dataVizColorScale = {
  50: "#60a5fa",
  100: "#ffb55a",
  200: "#8bd3c7",
  300: "#fd7f6f",
  400: "#b2e061",
  500: "#beb9db",
  600: "#bd7ebe",
  700: "#fdcce5",
  800: "#6EE7B7",
  900: "#ffee65",
}
const yellowColorScale = {
  50: "#fefce8",
  100: "#fef9c3",
  200: "#fef08a",
  300: "#fde047",
  400: "#facc15",
  500: "#eab308",
  600: "#ca8a04",
  700: "#a16207",
  800: "#854d0e",
  900: "#713f12",
}
const greenColorScale = {
  50: "#ECFDF5",
  100: "#D1FAE5",
  200: "#A7F3D0",
  300: "#6EE7B7",
  400: "#34D399",
  500: "#10B981",
  600: "#059669",
  700: "#047857",
  800: "#065F46",
  900: "#064E3B",
}
const redColorScale = {
  50: "#FEF2F2",
  100: "#FEE2E2",
  200: "#FECACA",
  300: "#FCA5A5",
  400: "#F87171",
  500: "#EF4444",
  600: "#DC2626",
  main: "#DC2626",
  700: "#B91C1C",
  800: "#991B1B",
  900: "#7F1D1D",
}
const fuchsiaColorScale = {
  50: "#FDF4FF",
  100: "#FAE8FF",
  200: "#F5D0FE",
  300: "#F0ABFC",
  400: "#E879F9",
  500: "#D946EF",
  600: "#C026D3",
  700: "#A21CAF",
  800: "#86198F",
  900: "#701A75",
}
const violetColorScale = {
  50: "#f5f3ff",
  100: "#ede9fe",
  200: "#ddd6fe",
  300: "#c4b5fd",
  400: "#a78bfa",
  500: "#8b5cf6",
  600: "#7c3aed",
  700: "#6d28d9",
  800: "#5b21b6",
  900: "#4c1d95",
}
const blueColorScale = {
  50: "#F0F9FF",
  100: "#E0F2FE",
  200: "#BAE6FD",
  300: "#7DD3FC",
  400: "#38BDF8",
  500: "#0EA5E9",
  600: "#0284C7",
  700: "#0369A1",
  800: "#075985",
  900: "#0C4A6E",
}
const grayColorScale = {
  50: "#f9fafb",
  100: "#f3f4f6",
  200: "#e5e7eb",
  300: "#d1d5db",
  400: "#9ca3af",
  500: "#6b7280",
  600: "#4b5563",
  main: "#4b5563",
  700: "#374151",
  800: "#1f2937",
  900: "#111827",
}

// -------------------------------
// Step 1: Define theme tokens
// -------------------------------
let customTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1200,
      xl: 1920,
    },
  },
  palette: {
    background: {
      default: "hsla(210, 20%, 99%, 1)",
      paper: "#fff",
    },
    // built in MUI color scales
    primary: primaryColorScale,
    secondary: grayColorScale,
    warning: yellowColorScale,
    // custom color scales
    dataViz: dataVizColorScale,
    yellow: yellowColorScale,
    green: greenColorScale,
    red: redColorScale,
    fuchsia: fuchsiaColorScale,
    violet: violetColorScale,
    blue: blueColorScale,
    gray: grayColorScale,
  },
  shape: {
    borderRadius: 6,
  },
  // overwrite the shadows since 25 is way too many options, use softer shadows so they don't get overdone
  shadows: [
    "none",
    "0 1px 2px 0 rgb(0 0 0 / 0.05)", // border-like
    "0 1px 3px rgb(44 42 38 / 7%), 0 4px 16px rgb(44 42 38 / 6%)", // xs
    "0 1px 4px rgb(44 42 38 / 7%), 0 4px 24px rgb(44 42 38 / 7%)", // sm
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)", // md
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", // lg
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)", // xl
  ] as any,
  transitions: {
    duration: {
      // for micro interactions
      short: 150,
      // basic (default) timing
      standard: 200,
      // for complex animations
      complex: 300,
    },
  },
  // allow styling individual fontSizes in a component with the fontSize prop: "xs", "sm", "md", etc.
  typography: {
    fontSize: 14,
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
    "5xl": "3rem",
    "6xl": "3.75rem",
    "7xl": "4.5rem",
    // use Inter, and then the default font stack
    fontFamily: [
      '"Inter"',
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    h1: {
      fontSize: "2rem",
      fontWeight: 800,
      lineHeight: 78 / 70,
    },
    h2: {
      fontSize: "1.25rem",
      fontWeight: 700,
      lineHeight: 44 / 36,
    },
    h3: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 44 / 36,
      letterSpacing: 0,
    },
    h4: {
      fontSize: "0.75rem",
      lineHeight: 42 / 28,
      letterSpacing: 0,
    },
  },
})

const borderRadiusInPx = Number(customTheme.shape.borderRadius)

// step 2 to use custom theme values:
customTheme = createTheme(customTheme, {
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          // eyeballed to match other inputs
          paddingBottom: `6px !important `,
          paddingLeft: `12px !important `,
          paddingRight: `32px !important `,
          paddingTop: `6px !important `,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: any }) => ({
          ...((ownerState.variant === "contained" ||
            ownerState.variant === "outlined") && {
            boxShadow: customTheme.shadows[1],
          }),
          ...(ownerState.variant === "contained" &&
            ownerState.color === "primary" && {
              transition: customTheme.transitions.create(`all`),
              fontWeight: 600,
              "&:hover": {
                filter: "contrast(1.1)",
              },
            }),
          ...(ownerState.variant === "outlined" &&
            ownerState.color === "gray" && {
              background: customTheme.palette.background.paper,
              borderColor: customTheme.palette.gray["300"],
            }),
          cursor: "pointer",
          borderRadius: borderRadiusInPx * 1.25,
          textTransform: "inherit",
          letterSpacing: 0.2,
        }),
      },
      variants: [
        {
          props: { variant: "soft" },
          style: {
            display: "flex",
            fontSize: "xs",
            fontWeight: 500,
            minWidth: 32,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: customTheme.spacing(1),
            paddingRight: customTheme.spacing(1),
            background: customTheme.palette.primary["50"],
            color: customTheme.palette.primary["600"],
            transition: customTheme.transitions.create(`all`),
            lineHeight: 1.5,
            svg: {
              height: 16,
              width: 16,
            },
            "&:hover": {
              background: customTheme.palette.primary["100"],
              color: customTheme.palette.primary["700"],
            },
            ".MuiButton-startIcon": {
              marginRight: customTheme.spacing(0.25),
            },
            " .MuiButton-endIcon": {
              marginLeft: customTheme.spacing(0.25),
            },
          },
        },
      ],
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          background: customTheme.palette.gray["100"],
          padding: `${customTheme.spacing(1)} ${customTheme.spacing(2)}`,
          justifyContent: "flex-end",
          color: customTheme.palette.gray["500"],
          transition: customTheme.transitions.create("background"),
          fontWeight: "500",
          "&:hover": {
            background: customTheme.palette.gray["50"],
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: customTheme.typography.xl,
          fontWeight: "500",
          color: customTheme.palette.gray["700"],
        },
        action: {
          alignSelf: "center",
          marginRight: 0,
          fontSize: customTheme.typography.sm,
          color: customTheme.palette.gray["500"],
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: checkboxIcon,
        indeterminateIcon: checkboxIndeterminateIcon,
        checkedIcon: checkboxCheckedIcon,
      },
      styleOverrides: {
        root: {
          borderRadius: borderRadiusInPx,
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { variant: "inverted" },
          style: {
            color: customTheme.palette.primary["500"],
            fontWeight: "500",
            transition: customTheme.transitions.create(`all`),
            background: customTheme.palette.primary["50"],
            "&:hover": {
              color: customTheme.palette.primary["600"],
              background: customTheme.palette.primary["100"],
            },
          },
        },
      ],
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadiusInPx * 2,
          padding: customTheme.spacing(3),
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          borderRadius: borderRadiusInPx * 1.5,
        },
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: "small",
        hiddenLabel: true,
      },
      styleOverrides: {
        root: {
          background: customTheme.palette.background.paper,
          backgroundColor: customTheme.palette.background.paper,
          borderColor: customTheme.palette.gray["300"],
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: borderRadiusInPx,
          boxShadow: `${customTheme.shadows[1]}`,
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: "filled",
      },
      styleOverrides: {
        root: {
          borderColor: customTheme.palette.gray["300"],
          minWidth: 100,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: customTheme.typography.sm,
          fontWeight: "600",
          color: customTheme.palette.gray["600"],
          "&.Mui-focused": {
            color: "inherit !important",
          },
        },
      },
    },
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          backgroundColor: customTheme.palette.background.paper,
          borderRadius: borderRadiusInPx,
          "&.Mui-focused": {
            backgroundColor: customTheme.palette.background.paper,
            borderColor: customTheme.palette.primary["400"],
          },
          "&:hover:not(.Mui-disabled)": {
            backgroundColor: customTheme.palette.background.paper,
          },
          "&.Mui-disabled": {
            backgroundColor: customTheme.palette.gray["100"],
          },
        },
      },
      variants: [
        {
          props: {
            variant: "small",
          },
          style: {
            ".MuiFilledInput-input": {
              paddingTop: customTheme.spacing(0.75),
              paddingBottom: customTheme.spacing(0.75),
            },
          },
        },
      ],
    },
    MuiGridFilterForm: {
      styleOverrides: {
        closeIcon: {
          minWidth: 30,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          ".MuiListItemText-primary": {
            fontWeight: "500",
            color: customTheme.palette.gray["700"],
          },
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        elevation: 2,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: customTheme.spacing(0.75),
          paddingBottom: customTheme.spacing(0.75),
          "&:hover": {
            backgroundColor: customTheme.palette.primary["subtle"],
          },
          "&:focus": {
            backgroundColor: customTheme.palette.primary["subtle"],
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: customTheme.palette.gray["300"],
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderColor: customTheme.palette.gray["200"],
          borderWidth: 1,
          borderStyle: "solid",
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        icon: radioUncheckedIcon,
        checkedIcon: radioCheckedIcon,
      },
    },
    MuiSelect: {
      variants: [
        {
          props: { variant: "small" },
          style: {
            paddingTop: 0,
          },
        },
      ],
      styleOverrides: {
        select: {
          minWidth: 100,
          borderRadius: borderRadiusInPx,
          "&:focus": {
            backgroundColor: customTheme.palette.background.paper,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: 2,
            transitionDuration: "300ms",
            "&.Mui-checked": {
              transform: "translateX(16px)",
              color: customTheme.palette.background.paper,
              "& + .MuiSwitch-track": {
                backgroundColor: "#65C466",
                opacity: 1,
                border: 0,
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.5,
              },
            },
            "&.Mui-focusVisible .MuiSwitch-thumb": {
              color: "#33cf4d",
              border: "6px solid #fff",
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: customTheme.palette.grey[100],
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.7,
            },
          },
          "& .MuiSwitch-thumb": {
            boxSizing: "border-box",
            width: 22,
            height: 22,
          },
          "& .MuiSwitch-track": {
            borderRadius: 26 / 2,
            backgroundColor: "#E9E9EA",
            opacity: 1,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          paddingLeft: 0,
          paddingRight: 0,
          minWidth: 60,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          gap: customTheme.spacing(4),
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          background: customTheme.palette.gray["800"],
        },
        arrow: {
          color: customTheme.palette.gray["800"],
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "inherit",
          "&.Mui-selected": {
            color: customTheme.palette.primary["700"],
          },
          "&.Mui-disabled": {
            color: customTheme.palette.gray["300"],
          },
        },
        sizeSmall: {
          paddingTop: customTheme.spacing(0.25),
          paddingBottom: customTheme.spacing(0.25),
          paddingLeft: customTheme.spacing(1),
          paddingRight: customTheme.spacing(1),
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: customTheme.palette.gray["800"],
      },
    },
  },
})

export { customTheme }

// -------------------------------
// Lastly, create a Theme Provider
// -------------------------------
export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const theme = customTheme

  return (
    <MuiThemeProvider theme={theme}>
      {/* Set global MUI styles */}
      <GlobalStyles
        styles={{
          html: {
            minHeight: "100vh",
          },
          body: {
            minHeight: "100vh",
          },
          "#__next": {
            minHeight: "100vh",
          },
          ".Mui-focusVisible": {
            boxShadow: `0 0 0 3px ${theme.palette.primary["200"]} !important`,
          },
          ".MuiDataGrid-row": {
            backgroundColor: theme.palette.background.paper,
          },
          ".MuiDataGrid-columnsContainer": {
            backgroundColor: theme.palette.gray["100"],
            borderTopLeftRadius: theme.shape.borderRadius,
            borderTopRightRadius: theme.shape.borderRadius,
          },
          ".MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.gray["100"],
            minHeight: "48px !important",
            maxHeight: "48px !important",
          },
          ".MuiDataGrid-virtualScroller": {
            marginTop: "48px !important",
          },
          ".MuiDataGrid-columnHeaderTitleContainer": {
            paddingLeft: "2px",
          },
          ".MuiDataGrid-cell--textRight": {
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "-0.05em",
          },
          ".MuiDataGrid-footerContainer": {
            marginTop: "-8px !important",
          },
          ".MuiTabs-indicator": {
            display: "flex",
            justifyContent: "center",
            backgroundColor: "transparent",
          },
          ".MuiPickersDay-root": {
            borderRadius: `${theme.shape.borderRadius}px !important`,
          },
          ".MuiTablePagination-select": {
            paddingBottom: `1px !important`,
          },
          ".MuiBreadcrumbs-separator": {
            color: theme.palette.grey["400"],
          },
          // change styles of G2Plot tooltips used in Ant Design charts
          ".g2-tooltip": {
            border: `1px solid ${customTheme.palette.gray["200"]} !important`,
            borderRadius: `${theme.shape.borderRadius} !important`,
            boxShadow: `${theme.shadows[2]} !important`,
            fontFamily: `${theme.typography.fontFamily} !important`,
          },
          ".daily-variant-stats-cell": {
            padding: "0 !important",
          },
          ".hot-toast": {
            padding: `${theme.spacing(1.5)} !important`,
            gap: theme.spacing(1.5),
            "[role='status']": {
              margin: 0,
            },
          },
          ".medium-zoom-overlay": {
            zIndex: zIndexMap.MEDIUM_ZOOM_OVERLAY,
          },
          "::selection": {
            background: "hsla(217deg 42% 95% / 1)",
          },
        }}
      />
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  )
}
